import { ChangeEvent, FormEvent, forwardRef, MouseEvent, useState } from "react";
import { AppButton, AppTextarea, Divider } from "@libs/zapi-ui/custom";
import { ArrowUpIcon, CheckIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useCommentStore } from "../model/store/commentsStore";
import { generateStubComment } from "../lib/generateStubComment";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useEditorStore } from "@/zapi-frontend/stores/editor";

type TReplyEditInputFieldProps = {
  firstName?: string;
  lastName?: string;
  userId: number;
  commentId: string;
  commentContent?: string;
  isEditField?: boolean;
  className?: string;
  parentId?: string;
  onSubmit?: () => void;
  onCancel: () => void;
};

export const ReplyEditInputField = forwardRef<HTMLTextAreaElement, TReplyEditInputFieldProps>((props, ref) => {
  const {
    userId,
    firstName = "",
    lastName = "",
    commentId,
    commentContent = "",
    isEditField = false,
    className = "",
    parentId,
    onSubmit,
    onCancel
  } = props;

  const { createComment, updateComment } = useCommentStore();
  const { activeChapter } = useChapterStore();
  const { selectedProject } = useProjectStore();
  const { commentsUpdated } = useEditorStore();

  const projectId = selectedProject?.id;
  const chapterId = activeChapter?.id.toString() || "";

  const [message, setMessage] = useState(commentContent);

  function handleInputChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setMessage(e.target.value);
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage || !projectId || !chapterId) return;

    setMessage("");
    if (typeof ref === "object" && ref?.current) ref.current.style.height = "auto";
    onSubmit?.();

    if (isEditField) {
      await updateComment({ projectId, chapterId, commentId, content: trimmedMessage, parentId });
      commentsUpdated?.();
      return;
    }

    await createComment({
      projectId,
      chapterId,
      content: trimmedMessage,
      stubComment: generateStubComment({ userId, firstName, lastName, content: trimmedMessage }),
      parentId: commentId
    });

    commentsUpdated?.();
  };

  function onInputHeightChange(e: FormEvent<HTMLTextAreaElement>) {
    const target = e.currentTarget as HTMLTextAreaElement;
    target.style.height = "auto";
    target.style.height = `${target.scrollHeight}px`;
  }

  function handleCancel(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setMessage("");
    onCancel?.();
  }

  return (
    <form
      onClick={(e) => e.stopPropagation()}
      onSubmit={handleSubmit}
      className={cn(
        "h-auto flex flex-col relative rounded-[8px] bg-white transition-all ease duration-300 bg-innoveraZinc-50",
        "border border-innoveraZinc-100",
        className
      )}
    >
      <AppTextarea
        ref={ref}
        fullWidth
        className="min-h-[44px] max-h-[80px] resize-none !border-0 !ring-0 py-3 px-4"
        placeholder={isEditField ? "Edit your comment" : "Reply"}
        value={message}
        onChange={handleInputChange}
        onInput={onInputHeightChange}
        rows={1}
        data-testid="comment-input"
      />

      <Divider className="mb-2 mx-auto !w-[calc(100%-26px)] !bg-innoveraZinc-150" />

      <div className="flex gap-1 ml-auto mr-3.5 mb-3">
        {message && (
          <AppButton
            className="border-innoveraZinc-150"
            appearance="outlined"
            color="zinc100"
            endAdornment={<XIcon className="size-3 text-innoveraZinc-400" />}
            size="iconSm"
            type="button"
            onClick={handleCancel}
          />
        )}
        <AppButton
          className="ml-auto"
          endAdornment={
            isEditField ? (
              <CheckIcon style={{ width: "12px", height: "12px" }} />
            ) : (
              <ArrowUpIcon style={{ width: "12px", height: "12px" }} />
            )
          }
          size="iconSm"
          type="submit"
          disabled={!message.trim()}
          data-testid="post-comment-button"
        />
      </div>
    </form>
  );
});
